import {
  type ChildProfile,
  LANGUAGES,
  type Language,
  MAX_FOREIGN_LANGUAGES,
} from '@kidshub/contracts';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip } from '../ui/kit.js';

export type ChildLanguageSettings = Pick<ChildProfile, 'nativeLanguage' | 'foreignLanguages'>;

interface ChildLanguagesProps {
  languages: ChildLanguageSettings;
  onChange: (languages: ChildLanguageSettings) => void;
}

/**
 * The languages a child has, and the ones a parent wants her to practise.
 *
 * Part of the child's settings draft rather than saved on its own. It used to
 * save each tap, which was right when it sat alone on the children list; on a
 * page with one save bar, a card that saves itself while the rest waits would
 * be the one control that does not do what the bar says.
 *
 * The native language is a list rather than chips: there are a dozen of them
 * and exactly one is chosen. Foreign languages are chips, several at once.
 */
export const ChildLanguages = ({ languages, onChange }: ChildLanguagesProps) => {
  const { t } = useTranslation();
  const nativeId = useId();
  const { nativeLanguage, foreignLanguages } = languages;
  const name = (language: Language) => t(`language.${language}`);

  const setNative = (language: Language) =>
    // The contract never stores the native language as a foreign one either;
    // the server would drop it, and the draft would then disagree with it.
    onChange({
      nativeLanguage: language,
      foreignLanguages: foreignLanguages.filter((entry) => entry !== language),
    });

  const toggleForeign = (language: Language) =>
    onChange({
      nativeLanguage,
      foreignLanguages: foreignLanguages.includes(language)
        ? foreignLanguages.filter((entry) => entry !== language)
        : [...foreignLanguages, language],
    });

  return (
    <Card title={t('children.languages')} meta={t('child.languagesMeta')} className="span-2">
      <div className="child-langs">
        <div className="lang-block field">
          <label htmlFor={nativeId}>{t('children.nativeLanguage')}</label>
          <select
            id={nativeId}
            className="input"
            value={nativeLanguage}
            onChange={(event) => setNative(event.target.value as Language)}
          >
            {LANGUAGES.map((language) => (
              <option key={language} value={language}>
                {name(language)}
              </option>
            ))}
          </select>
        </div>

        <div className="lang-block">
          <fieldset>
            <legend>{t('children.foreignLanguages')}</legend>
            <div className="chips">
              {LANGUAGES.filter((language) => language !== nativeLanguage).map((language) => {
                const isOn = foreignLanguages.includes(language);
                return (
                  <Chip
                    key={language}
                    selected={isOn}
                    disabled={!isOn && foreignLanguages.length >= MAX_FOREIGN_LANGUAGES}
                    onToggle={() => toggleForeign(language)}
                  >
                    {name(language)}
                  </Chip>
                );
              })}
            </div>
            <p className="help">{t('child.foreignLimit', { max: MAX_FOREIGN_LANGUAGES })}</p>
          </fieldset>
        </div>
      </div>
    </Card>
  );
};
